import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { flashCardsAPI } from '../api/flashCardsAPI';
import './FlashCardsPage.css';

const MAX_FILE_MB = 5;

export default function FlashCardsPage() {
  const { id: appId } = useParams();
  const navigate = useNavigate();
  const [decks, setDecks] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedDeckId, setSelectedDeckId] = useState(null);
  const [currentDeck, setCurrentDeck] = useState(null);
  const [loadingDecks, setLoadingDecks] = useState(true);
  const [loadingDeck, setLoadingDeck] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [cardIndex, setCardIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [renameFolderId, setRenameFolderId] = useState(null);
  const [renameFolderValue, setRenameFolderValue] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [uploadFolderId, setUploadFolderId] = useState('');
  const [openMenu, setOpenMenu] = useState(null); // 'deck-{id}' | 'folder-{id}'

  const closeMenus = () => {
    setOpenMenu(null);
  };

  const fetchDecks = useCallback(async () => {
    setLoadingDecks(true);
    setError(null);
    try {
      const [decksRes, foldersRes] = await Promise.all([
        flashCardsAPI.getDecks(appId || null),
        flashCardsAPI.getFolders(),
      ]);
      setDecks(decksRes.decks || []);
      setFolders(foldersRes.folders || []);
    } catch (e) {
      setError(e?.message || 'Failed to load your flashcard sets.');
    } finally {
      setLoadingDecks(false);
    }
  }, [appId]);

  useEffect(() => {
    fetchDecks();
  }, [fetchDecks]);

  useEffect(() => {
    if (!selectedDeckId) {
      setCurrentDeck(null);
      setCardIndex(0);
      setFlipped(false);
      return;
    }
    setLoadingDeck(true);
    setError(null);
    flashCardsAPI
      .getDeck(selectedDeckId)
      .then((deck) => {
        setCurrentDeck(deck);
        setCardIndex(0);
        setFlipped(false);
      })
      .catch((e) => setError(e?.message || 'Failed to load deck.'))
      .finally(() => setLoadingDeck(false));
  }, [selectedDeckId]);

  const handleUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`File must be under ${MAX_FILE_MB} MB.`);
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const res = await flashCardsAPI.uploadFile(file, appId || null, uploadFolderId || null);
      await fetchDecks();
      setSelectedDeckId(res.deck_id);
      const deck = await flashCardsAPI.getDeck(res.deck_id);
      setCurrentDeck(deck);
      setCardIndex(0);
      setFlipped(false);
    } catch (err) {
      const msg = (err?.data && err.data.error) ? err.data.error : (err?.message || 'Upload failed.');
      setError(msg);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleCreateFolder = async () => {
    const name = (newFolderName || '').trim() || 'New folder';
    setError(null);
    try {
      await flashCardsAPI.createFolder(name);
      setNewFolderName('');
      setShowNewFolderInput(false);
      await fetchDecks();
    } catch (err) {
      setError(err?.message || 'Failed to create folder.');
    }
  };

  const startRenameFolder = (folder) => {
    setRenameFolderId(folder.id);
    setRenameFolderValue(folder.name);
  };

  const handleRenameFolder = async () => {
    if (!renameFolderId || !renameFolderValue.trim()) {
      setRenameFolderId(null);
      return;
    }
    setError(null);
    try {
      await flashCardsAPI.updateFolder(renameFolderId, renameFolderValue.trim());
      setRenameFolderId(null);
      setRenameFolderValue('');
      await fetchDecks();
    } catch (err) {
      setError(err?.message || 'Failed to rename folder.');
    }
  };

  const handleMoveDeckToFolder = async (deckId, folderId) => {
    setError(null);
    setOpenMenu(null);
    try {
      await flashCardsAPI.updateDeck(deckId, { folder_id: folderId || null });
      await fetchDecks();
    } catch (err) {
      setError(err?.message || 'Failed to move set.');
    }
  };

  const handleDeleteDeck = async (deckId) => {
    if (!window.confirm('Delete this flashcard set? This cannot be undone.')) return;
    try {
      await flashCardsAPI.deleteDeck(deckId);
      await fetchDecks();
      if (selectedDeckId === deckId) {
        setSelectedDeckId(null);
        setCurrentDeck(null);
      }
    } catch (err) {
      setError(err?.message || 'Failed to delete.');
    }
  };

  const cards = currentDeck?.flashcards || [];
  const currentCard = cards[cardIndex];
  const totalCards = cards.length;

  const decksByFolder = React.useMemo(() => {
    const uncategorized = [];
    const byFolder = {};
    folders.forEach((f) => { byFolder[f.id] = []; });
    decks.forEach((d) => {
      if (!d.folder_id) uncategorized.push(d);
      else if (byFolder[d.folder_id]) byFolder[d.folder_id].push(d);
      else uncategorized.push(d);
    });
    return { uncategorized, byFolder };
  }, [decks, folders]);

  const renderDeckItem = (d) => {
    const menuOpen = openMenu === `deck-${d.id}`;
    return (
      <div
        key={d.id}
        className={`FlashCardsPage__sidebarItemWrap ${selectedDeckId === d.id ? 'FlashCardsPage__sidebarItem--active' : ''}`}
      >
        <button
          type="button"
          className="FlashCardsPage__sidebarItem"
          onClick={() => setSelectedDeckId(d.id)}
        >
          <span className="FlashCardsPage__sidebarItemTitle">{d.title}</span>
          <span className="FlashCardsPage__sidebarItemMeta">
            {d.flashcard_count} cards
            {d.created_at ? ` · ${new Date(d.created_at).toLocaleDateString()}` : ''}
          </span>
        </button>
        <div className="FlashCardsPage__deckActions">
          <button
            type="button"
            className="FlashCardsPage__dotsBtn"
            onClick={(ev) => {
              ev.stopPropagation();
              setOpenMenu(menuOpen ? null : `deck-${d.id}`);
            }}
            title="Actions"
            aria-label="Actions"
            aria-expanded={menuOpen}
          >
            <span className="FlashCardsPage__dotsIcon" aria-hidden>⋮</span>
          </button>
          {menuOpen && (
            <>
              <div className="FlashCardsPage__backdrop" onClick={closeMenus} aria-hidden />
              <div className="FlashCardsPage__folderMenu FlashCardsPage__menu" role="menu">
                <div className="FlashCardsPage__menuSection">Add to folder</div>
                <button type="button" role="menuitem" onClick={() => handleMoveDeckToFolder(d.id, null)}>No folder</button>
                {folders.map((f) => (
                  <button key={f.id} type="button" role="menuitem" onClick={() => handleMoveDeckToFolder(d.id, f.id)}>{f.name}</button>
                ))}
                <div className="FlashCardsPage__menuDivider" />
                <button type="button" role="menuitem" className="FlashCardsPage__menuItemDanger" onClick={() => { closeMenus(); handleDeleteDeck(d.id); }}>Delete set</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="FlashCardsPage">
      <header className="FlashCardsPage__header">
        <h1 className="FlashCardsPage__title">Flash Cards</h1>
      </header>

      <div className="FlashCardsPage__layout">
        <aside className="FlashCardsPage__sidebar">
          <h2 className="FlashCardsPage__sidebarTitle">My flashcard sets</h2>
          <div className="FlashCardsPage__sidebarList">
            {loadingDecks ? (
              <div className="FlashCardsPage__loading">Loading...</div>
            ) : (
              <>
                {decksByFolder.uncategorized.length > 0 && (
                  <div className="FlashCardsPage__folderGroup">
                    <div className="FlashCardsPage__folderGroupTitle">Uncategorized</div>
                    {decksByFolder.uncategorized.map(renderDeckItem)}
                  </div>
                )}
                {folders.map((folder) => (
                  <div key={folder.id} className="FlashCardsPage__folderGroup">
                    {renameFolderId === folder.id ? (
                      <div className="FlashCardsPage__folderRename">
                        <input
                          type="text"
                          value={renameFolderValue}
                          onChange={(e) => setRenameFolderValue(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleRenameFolder()}
                          autoFocus
                          className="FlashCardsPage__folderRenameInput"
                        />
                        <button type="button" className="FlashCardsPage__actionBtn" onClick={handleRenameFolder}>Save</button>
                        <button type="button" className="FlashCardsPage__actionBtn" onClick={() => { setRenameFolderId(null); setRenameFolderValue(''); }}>Cancel</button>
                      </div>
                    ) : (
                      <div className="FlashCardsPage__folderRow">
                        <span className="FlashCardsPage__folderGroupTitle">{folder.name}</span>
                        <div className="FlashCardsPage__folderRowActions">
                          <button
                            type="button"
                            className="FlashCardsPage__dotsBtn"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setOpenMenu(openMenu === `folder-${folder.id}` ? null : `folder-${folder.id}`);
                            }}
                            title="Folder actions"
                            aria-label="Folder actions"
                            aria-expanded={openMenu === `folder-${folder.id}`}
                          >
                            <span className="FlashCardsPage__dotsIcon" aria-hidden>⋮</span>
                          </button>
                          {openMenu === `folder-${folder.id}` && (
                            <>
                              <div className="FlashCardsPage__backdrop" onClick={closeMenus} aria-hidden />
                              <div className="FlashCardsPage__folderMenu FlashCardsPage__menu" role="menu">
                                <button type="button" role="menuitem" onClick={() => { startRenameFolder(folder); closeMenus(); }}>Rename</button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    {decksByFolder.byFolder[folder.id]?.map(renderDeckItem) || null}
                  </div>
                ))}
                {decks.length === 0 && !loadingDecks && (
                  <div className="FlashCardsPage__empty">No sets yet. Create one below.</div>
                )}
              </>
            )}
          </div>
          <div className="FlashCardsPage__sidebarNew">
            {showNewFolderInput ? (
              <div className="FlashCardsPage__newFolderRow">
                <input
                  type="text"
                  placeholder="Folder name"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                  className="FlashCardsPage__folderRenameInput"
                />
                <button type="button" className="FlashCardsPage__actionBtn" onClick={handleCreateFolder}>Add</button>
                <button type="button" className="FlashCardsPage__actionBtn" onClick={() => { setShowNewFolderInput(false); setNewFolderName(''); }}>Cancel</button>
              </div>
            ) : (
              <button type="button" className="FlashCardsPage__newFolderBtn" onClick={() => setShowNewFolderInput(true)}>
                + New folder
              </button>
            )}
            <div className="FlashCardsPage__uploadFolderChoice">
              <label className="FlashCardsPage__uploadFolderLabel">Save new sets to:</label>
              <select
                value={uploadFolderId}
                onChange={(e) => setUploadFolderId(e.target.value)}
                className="FlashCardsPage__folderSelect"
              >
                <option value="">No folder</option>
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
            <label className="FlashCardsPage__uploadZone" style={{ margin: 0, padding: '12px 16px', display: 'block' }}>
              <input type="file" accept=".pdf,.txt,.md,.docx" onChange={handleUpload} disabled={uploading} />
              <span>{uploading ? 'Processing...' : 'New set from file (PDF, TXT, MD, DOCX)'}</span>
            </label>
          </div>
        </aside>

        <main className="FlashCardsPage__main">
          {error && <div className="FlashCardsPage__error">{error}</div>}

          {uploading && !currentDeck && (
            <div className="FlashCardsPage__uploading">Creating your flashcard set...</div>
          )}

          {!uploading && !selectedDeckId && !currentDeck && (
            <>
              <div className="FlashCardsPage__uploadZone">
                <label>
                  <input type="file" accept=".pdf,.txt,.md,.docx" onChange={handleUpload} />
                  Choose a file to create a new set (max {MAX_FILE_MB} MB)
                </label>
                <p>PDF, Word, or text. We’ll generate notes and flashcards.</p>
              </div>
              {decks.length > 0 && (
                <p className="FlashCardsPage__empty">Select a set from the left to study.</p>
              )}
            </>
          )}

          {selectedDeckId && loadingDeck && (
            <div className="FlashCardsPage__loading">Loading set...</div>
          )}

          {currentDeck && !loadingDeck && (
            <div className="FlashCardsPage__deckView">
              <div className="FlashCardsPage__cardPanel">
                {totalCards === 0 ? (
                  <p className="FlashCardsPage__empty">No cards in this set.</p>
                ) : (
                  <>
                    <div
                      className="FlashCardsPage__card"
                      onClick={() => setFlipped((f) => !f)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && setFlipped((f) => !f)}
                    >
                      {flipped ? currentCard.back : currentCard.front}
                    </div>
                    <p className="FlashCardsPage__cardHint">Click to flip</p>
                    <div className="FlashCardsPage__nav">
                      <button
                        type="button"
                        className="FlashCardsPage__navBtn"
                        disabled={cardIndex === 0}
                        onClick={() => { setCardIndex((i) => i - 1); setFlipped(false); }}
                      >
                        Previous
                      </button>
                      <span className="FlashCardsPage__progress">
                        {cardIndex + 1} / {totalCards}
                      </span>
                      <button
                        type="button"
                        className="FlashCardsPage__navBtn"
                        disabled={cardIndex >= totalCards - 1}
                        onClick={() => { setCardIndex((i) => i + 1); setFlipped(false); }}
                      >
                        Next
                      </button>
                    </div>
                    <button
                      type="button"
                      className="FlashCardsPage__navBtn"
                      style={{ marginTop: 12 }}
                      onClick={() => handleDeleteDeck(currentDeck.id)}
                    >
                      Delete this set
                    </button>
                  </>
                )}
              </div>
              <div className="FlashCardsPage__notesPanel">
                <h3 className="FlashCardsPage__notesTitle">Notes</h3>
                <div className="FlashCardsPage__notesContent">
                  {currentDeck.notes || 'No notes for this set.'}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
